import { useRef, useState } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { X } from "lucide-react";

const RATIOS = [
  { label: "Libre", value: undefined },
  { label: "1:1", value: 1 },
  { label: "16:9", value: 16 / 9 },
  { label: "4:5", value: 4 / 5 },
  { label: "3:2", value: 3 / 2 },
];

const centerAspect = (w, h, aspect) =>
  centerCrop(makeAspectCrop({ unit: "%", width: 90 }, aspect, w, h), w, h);

const freeCrop = () => ({ unit: "%", x: 5, y: 5, width: 90, height: 90 });

export default function ImageCropper({ src, filename = "image.jpg", onCancel, onCropped, busy }) {
  const imgRef = useRef(null);
  const [crop, setCrop] = useState();
  const [completed, setCompleted] = useState(null);
  const [aspect, setAspect] = useState(undefined);

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    setCrop(aspect ? centerAspect(width, height, aspect) : freeCrop());
  };

  const applyAspect = (a) => {
    setAspect(a);
    const img = imgRef.current;
    if (img) setCrop(a ? centerAspect(img.width, img.height, a) : freeCrop());
  };

  const confirm = async () => {
    const image = imgRef.current;
    if (!image || !completed?.width) return;
    try {
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(completed.width * scaleX));
      canvas.height = Math.max(1, Math.round(completed.height * scaleY));
      const ctx = canvas.getContext("2d");
      ctx.drawImage(
        image,
        completed.x * scaleX, completed.y * scaleY,
        completed.width * scaleX, completed.height * scaleY,
        0, 0, canvas.width, canvas.height
      );
      const isPng = /\.png$/i.test(filename);
      const blob = await new Promise((res) => canvas.toBlob(res, isPng ? "image/png" : "image/jpeg", 0.92));
      onCropped(blob || null);
    } catch (err) {
      onCropped(null);
    }
  };

  return (
    <div data-testid="image-cropper" className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4">
      <div className="bg-black border border-[#292929] max-w-3xl w-full max-h-[92vh] overflow-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#292929]">
          <div className="text-[11px] tracking-[0.3em] uppercase">Recadrer l'image</div>
          <button data-testid="cropper-close" onClick={onCancel} className="text-muted-eff hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6">
          <div className="flex flex-wrap gap-2 mb-5">
            {RATIOS.map((r) => (
              <button
                key={r.label}
                data-testid={`cropper-ratio-${r.label}`}
                onClick={() => applyAspect(r.value)}
                className={`text-[10px] tracking-widest uppercase px-3 py-1.5 border transition-colors duration-300 ${
                  aspect === r.value ? "border-white text-white" : "border-[#292929] text-muted-eff hover:border-white"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="flex justify-center bg-[#0a0a0a] border border-[#1a1a1a] p-2">
            <ReactCrop crop={crop} onChange={(c) => setCrop(c)} onComplete={(c) => setCompleted(c)} aspect={aspect}>
              <img ref={imgRef} src={src} alt="" crossOrigin="anonymous" onLoad={onImageLoad} style={{ maxHeight: "58vh" }} />
            </ReactCrop>
          </div>
          <div className="flex items-center justify-between mt-6">
            <span className="text-[10px] text-muted-eff">Glisse pour déplacer · tire les coins pour redimensionner</span>
            <div className="flex gap-3">
              <button data-testid="cropper-cancel" onClick={onCancel} className="text-xs tracking-widest uppercase px-5 py-2.5 border border-[#292929] hover:border-white transition-colors duration-300">
                Annuler
              </button>
              <button
                data-testid="cropper-confirm"
                onClick={confirm}
                disabled={busy}
                className="text-xs tracking-widest uppercase px-6 py-2.5 bg-white text-black hover:bg-[#dddddd] transition-colors duration-300 disabled:opacity-50"
              >
                {busy ? "..." : "Recadrer et enregistrer"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
